import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppConfig } from "@/hooks/useAppConfig";
import NumberTicker from "./effects/NumberTicker";
import MagnetButton from "./effects/MagnetButton";
import StreamText from "./effects/StreamText";
import { GalleryBlurSection } from "./GalleryBlurSection";
import { TestimonialsSection } from "./TestimonialsSection";

interface ChannelCardsProps {
  t: Record<string, string>;
  active: boolean;
}

const ease = [0.2, 0.8, 0.2, 1] as const;
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
  exit: { transition: { staggerChildren: 0.05, staggerDirection: -1 } },
};
const fadeSlideUp = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.25 } },
};
const cardReveal = {
  hidden: { opacity: 0, y: 30, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease, delay: 0.2 } },
  exit: { opacity: 0, y: -14, scale: 0.97, transition: { duration: 0.3 } },
};
const freeCardReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay: 0.4 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

export const ChannelCards: React.FC<ChannelCardsProps> = ({ t, active }) => {
  const { config } = useAppConfig();
  const botUsername = config.bot.botUsername || "";
  const channelPrice = config.pricing.vipYearly || 50000;
  const channelCurrency = config.pricing.currency || "COP";
  const channelLink = config.channel.freeChannelInviteLink || config.channel.inviteLink || "";
  const tickerKeyRef = useRef(0);
  const streamKeyRef = useRef(0);

  if (active) { tickerKeyRef.current++; streamKeyRef.current++; }

  const openBotChat = () => {
    window.dispatchEvent(new CustomEvent('telegram-command', { detail: { command: 'sub' } }));
  };

  const openChannel = () => {
    if (!channelLink) return;
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openTelegramLink(channelLink);
    } else {
      window.open(channelLink, "_blank");
    }
  };

  return (
    <div className="absolute inset-0 overflow-y-auto" style={{ color: 'var(--c-text)', fontFamily: 'var(--ff-body)' }}>
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key="channels-content"
            variants={stagger}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0 overflow-y-auto"
          >
            {/* Header */}
            <motion.div
              variants={fadeSlideUp}
              style={{ position: 'absolute', top: 'var(--safe-top, 62px)', left: 18, right: 18, paddingTop: 'var(--sp-4)' }}
            >
              <div style={{
                fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'var(--c-crimson)', fontWeight: 600,
              }}>
                <StreamText key={`ch-eb-${streamKeyRef.current}`} text={t.channels_tag || "Canales · 02 disponibles"} speed={30} delay={100} caret={false} />
              </div>
              <h2 style={{
                fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-title)',
                margin: '8px 0 0 0', lineHeight: 0.88, textTransform: 'uppercase',
              }}>
                {t.channels_title_1 || "Elige tu"}<br/>
                <span style={{
                  fontFamily: 'var(--ff-italic)', fontStyle: 'italic',
                  fontSize: 'clamp(38px, 6vw, 50px)', color: 'var(--c-crimson)',
                  letterSpacing: '-0.01em', textTransform: 'none',
                }}>{t.channels_title_2 || "experiencia."}</span>
              </h2>
            </motion.div>

            {/* VIP card */}
            <motion.div
              variants={cardReveal}
              whileHover={{ y: -2, transition: { duration: 0.2 } }}
              style={{
                position: 'absolute', top: 'calc(var(--safe-top, 62px) + 160px)',
                left: 18, right: 18,
                background: 'linear-gradient(160deg, var(--c-crimson-ink) 0%, var(--c-surface) 70%)',
                border: '1px solid rgb(var(--c-crimson-rgb) / 0.4)',
                padding: '20px 20px 22px', overflow: 'hidden',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
              }}
            >
              <div style={{
                position: 'absolute', top: 0, right: 0,
                background: 'var(--c-crimson)', color: 'var(--c-on-crimson)', padding: '4px 10px',
                fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700,
              }}>VIP</div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-heading)', letterSpacing: '0.04em' }}>{t.channels_vip_title || "EXCLUSIVO"}</span>
                <span style={{ fontFamily: 'var(--ff-italic)', fontStyle: 'italic', fontSize: 14, color: 'var(--c-crimson)' }}>{t.channels_vip_subtitle || "· privado"}</span>
              </div>
              <p style={{ margin: '8px 0 16px 0', fontSize: 'var(--fs-body)', lineHeight: 1.5, color: 'var(--c-text-dim)', maxWidth: 280 }}>
                <StreamText key={`ch-desc-${streamKeyRef.current}`} text={t.channels_vip_desc || "Acceso ilimitado al canal premium, drops semanales y chat 1-a-1."} speed={22} delay={500} caret={false} />
              </p>

              <div style={{
                display: 'flex', gap: 18, marginBottom: 18,
                borderTop: '1px solid var(--c-line)', borderBottom: '1px solid var(--c-line)', padding: '12px 0',
              }}>
                {[['50+', t.channels_stat_videos || 'videos'], ['7d', t.channels_stat_drop || 'cada drop'], ['1:1', t.channels_stat_dm || 'DM']].map(([n, l]) => (
                  <div key={l}>
                    <div style={{ fontFamily: 'var(--ff-display)', fontSize: 22, letterSpacing: '0.03em', lineHeight: 1 }}>{n}</div>
                    <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--c-muted)', marginTop: 4 }}>{l}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--c-muted)' }}>{t.channels_from || "Desde"}</div>
                  <div style={{
                    fontFamily: 'var(--ff-display)', fontSize: 'clamp(24px, 4vw, 32px)',
                    letterSpacing: '0.02em', lineHeight: 1, marginTop: 4,
                  }}>
                    <NumberTicker
                      key={`vip-price-${tickerKeyRef.current}`}
                      to={channelPrice}
                      format={n => {
                        if (channelCurrency === 'COP') return '$' + Math.round(n).toLocaleString('es-CO');
                        return '$' + Math.round(n).toLocaleString('en-US');
                      }}
                    />
                    <span style={{ fontSize: 14, color: 'var(--c-muted)', marginLeft: 6 }}>{channelCurrency}</span>
                  </div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--c-muted)', marginTop: 4 }}>{t.channels_duration || "30 días · renovable"}</div>
                </div>
                <MagnetButton
                  onClick={openBotChat}
                  className="glow-button"
                  style={{
                    background: 'var(--c-crimson)', color: 'var(--c-on-crimson)', border: 'none',
                    padding: '12px 18px', fontSize: 11,
                    letterSpacing: '0.18em', textTransform: 'uppercase' as const,
                    fontWeight: 700, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                >
                  {t.channels_contact_btn || "Contactar"} <span>→</span>
                </MagnetButton>
              </div>
            </motion.div>

            {/* Free card */}
            <motion.div
              variants={freeCardReveal}
              className="hover-lift"
              whileHover={{ borderColor: 'var(--c-line-hover)', transition: { duration: 0.2 } }}
              onClick={openChannel}
              role="link"
              tabIndex={0}
              aria-label="Canal gratuito"
              style={{
                position: 'absolute', top: 'calc(var(--safe-top, 62px) + 480px)',
                left: 18, right: 18,
                border: '1px solid var(--c-line)',
                padding: '18px 20px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'var(--c-surface)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                cursor: 'pointer',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--ff-display)', fontSize: 22, letterSpacing: '0.04em' }}>{t.channels_free_title || "CANAL FREE"}</span>
                  <span style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.2em', color: 'var(--c-muted)' }}>{t.channels_free_badge || "GRATIS"}</span>
                </div>
                <p style={{ margin: '6px 0 0 0', fontSize: 'var(--fs-body)', color: 'var(--c-text-dim)' }}>{t.channels_free_desc || "Previews, novedades y comunidad."}</p>
              </div>
              <span style={{ color: 'var(--c-text)', fontSize: 18, opacity: 0.6 }}>↗</span>
            </motion.div>

            {/* Extra Sections: Gallery Blur Preview (Feature #13) & Testimonials (Feature #16) */}
            <div style={{ position: 'absolute', top: 'calc(var(--safe-top, 62px) + 575px)', left: 0, right: 0, paddingBottom: 120 }}>
              <GalleryBlurSection onSubscribeClick={openBotChat} />
              <TestimonialsSection />
            </div>

            {/* Footer Meta */}
            <motion.div variants={fadeSlideUp} style={{ position: 'absolute', bottom: 90, left: 18 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                fontFamily: 'var(--ff-body)', color: 'var(--c-text-dim)',
                fontSize: 'var(--fs-caption)', letterSpacing: '0.18em', textTransform: 'uppercase',
              }}>
                <span style={{ fontFamily: 'var(--ff-display)', fontSize: 22, color: 'var(--c-text)', letterSpacing: '0.02em', lineHeight: 1 }}>03</span>
                <span style={{ color: 'var(--c-muted)' }}>/ 05</span>
                <span style={{ width: 24, height: 1, background: 'var(--c-line)', margin: '0 6px', display: 'inline-block' }}></span>
                <span>{t.channels_footer || "Canales"}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChannelCards;
